'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Upload, X, Loader2 } from 'lucide-react';
import {toast} from "@/hooks/use-toast";

// Schéma de validation pour le formulaire
const formSchema = z.object({
    fullName: z.string().min(2, { message: 'Le nom doit contenir au moins 2 caractères' }),
    email: z.string().email({ message: 'Veuillez entrer une adresse email valide' }),
    message: z.string().min(10, { message: 'Votre message doit contenir au moins 10 caractères' }),
    media: z.array(z.instanceof(File)).optional(),
});

type FormValues = z.infer<typeof formSchema>;

export default function CulinaryExperienceForm() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            fullName: '',
            email: '',
            message: '',
            media: [],
        },
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const fileArray = Array.from(files);
            setSelectedFiles(prev => [...prev, ...fileArray]);
            form.setValue('media', [...selectedFiles, ...fileArray]);
        }
    };

    const removeFile = (index: number) => {
        const newFiles = [...selectedFiles];
        newFiles.splice(index, 1);
        setSelectedFiles(newFiles);
        form.setValue('media', newFiles);
    };

    const onSubmit = async (data: FormValues) => {
        setIsSubmitting(true);

        try {
            // Créer un FormData pour envoyer les fichiers
            const formData = new FormData();
            formData.append('fullName', data.fullName);
            formData.append('email', data.email);
            formData.append('message', data.message);

            // Ajouter les fichiers
            if (selectedFiles.length > 0) {
                selectedFiles.forEach((file, index) => {
                    formData.append(`media_${index}`, file);
                });
            }

            // Envoyer les données au serveur
            const response = await fetch('/api/culinary-experiences', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Une erreur est survenue lors de l\'envoi du formulaire');
            }

            // Réinitialiser le formulaire
            form.reset();
            setSelectedFiles([]);

            // Afficher un message de succès
            toast({
                title: 'Merci pour votre partage !',
                description: 'Votre expérience culinaire a été enregistrée avec succès.',
            });
        } catch (error) {
            console.error('Erreur lors de l\'envoi du formulaire:', error);
            toast({
                title: 'Erreur',
                description: 'Une erreur est survenue lors de l\'envoi du formulaire. Veuillez réessayer.',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={"text-yellow-400 font-bold"}>Nom et prénoms</FormLabel>
                            <FormControl>
                                <Input placeholder="Votre nom complet" className={"bg-white"} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={"text-yellow-400 font-bold"}>Email</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="votre.email@exemple.com" className={"bg-white"} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={"text-yellow-400 font-bold"}>Message</FormLabel>
                            <FormControl>
                                <Textarea
                                    placeholder="Partagez votre expérience culinaire ivoirienne..."
                                    className="min-h-32 bg-white"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormItem>
                    <FormLabel className={"text-yellow-400 font-bold"}>Chargez des photos / vidéos</FormLabel>
                    <FormControl>
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => document.getElementById('file-upload')?.click()}
                                    className="flex items-center gap-2"
                                >
                                    <Upload size={16} />
                                    Sélectionner des fichiers
                                </Button>
                                <Input
                                    id="file-upload"
                                    type="file"
                                    multiple
                                    accept="image/*,video/*"
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                                <FormDescription className={"text-white"}>
                                    Formats acceptés: JPG, PNG, MP4, MOV (max 10MB)
                                </FormDescription>
                            </div>

                            {/* Aperçu des fichiers sélectionnés */}
                            {selectedFiles.length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="relative group">
                                            <div className="aspect-video bg-slate-100 rounded-md overflow-hidden">
                                                {file.type.startsWith('image/') ? (
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt={`Aperçu ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-slate-200">
                                                        <span className="text-sm text-slate-600">{file.name}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                aria-label="Supprimer"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </FormControl>
                    <FormMessage />
                </FormItem>

                <Button type="submit" className="w-full" variant={"yellow"} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Envoi en cours...
                        </>
                    ) : (
                        'Partager mon expérience'
                    )}
                </Button>
            </form>
        </Form>
    );
}
